import type { IColumnsPickerLiteProps } from "@similarweb/ui-components";

import type { SWSettings } from "app/common/services/swSettings";
import { EGraphGranularities } from "app/common/keyword-research/serp-players/consts";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { keywordGroupsQueryClient } from "app/services/keyword-groups";

const isMonthsToDateSupported = ({ duration, visitsBelowThreshold }) =>
    [BasicDurations.LAST_THREE_MONTHS, BasicDurations.LAST_MONTH].includes(duration) && !visitsBelowThreshold;

const getInitialGranularity = ({ duration, isMonthsToDateActive, isWeeklyKeywordsAvailable }) => {
    if (!isWeeklyKeywordsAvailable) {
        return EGraphGranularities.MONTHLY;
    }

    if (DurationService.isLessThanOrEqualToOneMonths(duration)) {
        return EGraphGranularities.WEEKLY;
    }
    if (isMonthsToDateActive) {
        return EGraphGranularities.WEEKLY;
    }
    return EGraphGranularities.MONTHLY;
};

export const isWeeklyKeywordsAvailable = (swSettings: SWSettings) => swSettings.components.WeeklyKeywords.isAllowed;

const isMonthlyGranularityDisable = (duration) => DurationService.isLessThanOrEqualToOneMonths(duration);

const changeMonthToDateUrlState = (newValue) => proNavigator.applyUpdateParams({ mtd: newValue });

const mtdToggleClickedCallback = (monthToDataValue) => changeMonthToDateUrlState(monthToDataValue);

const onTimeGranularityToggleCallback = (isMonthlyGranularity) => {
    if (isMonthlyGranularity) {
        changeMonthToDateUrlState(false);
    }
};

const onCalculateVisitsTrend =
    (setVisitsBelowThresholdCallback, avgVisitThreshold = 5000) =>
    ({ calculatedAvgVisits }) => {
        if (calculatedAvgVisits < avgVisitThreshold) {
            setVisitsBelowThresholdCallback(true);
            changeMonthToDateUrlState(false);
        }
    };

export const getCommonProps = async (params, webSourceFromParams = false) => {
    const { country, keyword, isWWW, duration, webSource } = params;
    const { from, to, isWindow } = DurationService.getDurationData(duration).forAPI;
    const isKeywordsGroup = keyword?.substring(0, 1) === "*";
    const keys = isKeywordsGroup ? keyword.substring(1) : keyword;
    const { GroupHash } = (await keywordGroupsQueryClient.getGroupById(keyword?.substring(1))) || {};
    const queryParams = {
        country,
        from,
        includeSubDomains: isWWW === "*",
        isWindow,
        keys,
        to,
        webSource: webSourceFromParams ? webSource : devicesTypes.TOTAL,
        GroupHash,
        sourceType: undefined,
        ...(duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS ? { latest: "28d" } : undefined),
    };
    return { queryParams, isKeywordsGroup, GroupHash };
};

const onSelectedTabChange = (setSelectedTabIndex) => (index) => {
    const tab = index === 0 ? "domains" : "keywords";
    allTrackers.trackEvent("Tab", "click", `Table/${tab}`);
    setSelectedTabIndex(index);
    proNavigator.applyUpdateParams({ tab });
};

export const getColumnsPickerLiteProps = (
    tableColumns,
    onClickToggleColumns,
): Omit<IColumnsPickerLiteProps, "translations"> => {
    const columns = tableColumns.reduce((res, col) => {
        if (!col.fixed) {
            return [
                ...res,
                {
                    key: col.field,
                    displayName: col.displayName,
                    visible: col.visible,
                },
            ];
        }
        return res;
    }, []);
    return {
        columns,
        onColumnToggle: onClickToggleColumns,
        onPickerToggle: () => null,
    };
};
